import { DOCUMENT } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import {
  animationFrameScheduler,
  filter,
  observeOn,
  skip,
  Subject,
  takeUntil,
} from 'rxjs';
import { LoadingController } from './shared/helpers/loading.controller';
import { AppConfigService } from './shared/services/app-config.service';
import { AuthService } from './shared/services/auth.service';
import { ToastService } from './shared/services/toast.service';

import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt-PT';
import { ScreenService } from './shared/services/screen.service';
import { EsriUtils } from './shared/utils/esri.utils';
import { TranslationService } from '@services/translation.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  loading = false;

  private lifecycle = new Subject<void>();

  constructor(
    private router: Router,
    private authService: AuthService,
    private appConfig: AppConfigService,
    private screenService: ScreenService,
    private toastService: ToastService,
    private loadingController: LoadingController,
    @Inject(LOCALE_ID) private localeId,
    @Inject(DOCUMENT) private _document: Document,
    private ts: TranslationService
  ) {
    registerLocaleData(localePt, localeId);

    _document.querySelector('html')?.setAttribute('lang', localeId);

    EsriUtils.setLocale(localeId);
  }

  ngOnInit(): void {
    if (this.appConfig.internalMode) {
      this.authService
        .observeEsriAuthenticated()
        .pipe(
          skip(1),
          filter((a) => !a),
          takeUntil(this.lifecycle)
        )
        .subscribe(() => {
          this.authService.requireEsriAuthInfoAsync();
        });
    }

    this.loadingController.changes
      .pipe(observeOn(animationFrameScheduler), takeUntil(this.lifecycle))
      .subscribe((loading) => {
        this.loading = loading;
      });

    this.router.events.pipe(takeUntil(this.lifecycle)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loadingController.show();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.loadingController.hide();

        if (event instanceof NavigationError) {
          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-load-page']
          );
        }
      }
    });

    const mobileModeClass = 'mobile-mode';
    this.screenService
      .observeIsMobile()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((isMobile) => {
        const body = this._document.body;
        if (isMobile) {
          body.classList.add(mobileModeClass);
        } else {
          body.classList.remove(mobileModeClass);
        }
      });
  }

  ngOnDestroy() {
    this.lifecycle.next();
    this.lifecycle.complete();
  }
}
